import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Plane } from 'lucide-react';
import { useTrips } from '../context/TripsContext';

export const NewTrip = (): React.ReactNode => {
  const navigate = useNavigate();
  const { createTrip } = useTrips();

  const [tripNickname, setTripNickname] = useState('Trip #1');
  const [loading, setLoading] = useState(false);

  const handleStartTracking = async () => {
    if (!tripNickname.trim()) return;

    try {
      setLoading(true);
      const newTrip = await createTrip({
        nickname: tripNickname,
        currency: 'USD',
        total_budget: 0,
      });
      navigate(`/trip/${newTrip.short_id}`);
    } catch (error) {
      console.error('Error creating trip:', error);
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="w-full max-w-2xl shadow-2xl">
        <CardHeader className="text-center space-y-4 pb-8">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-400 rounded-full blur-xl opacity-50"></div>
              <div className="relative bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full p-6">
                <Plane className="h-16 w-16 text-white" />
              </div>
            </div>
          </div>
          <CardTitle className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Start Your Journey
          </CardTitle>
          <CardDescription className="text-lg text-muted-foreground">
            Track your travel expenses and budget in one place
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-8">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="trip-name" className="text-lg font-semibold flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-500" />
                Trip Name
              </Label>
              <Input
                id="trip-name"
                type="text"
                value={tripNickname}
                onChange={(e) => setTripNickname(e.target.value)}
                className="text-2xl font-semibold h-16 px-4"
                placeholder="Trip #1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleStartTracking();
                  }
                }}
              />
            </div>
          </div>

          <Button
            onClick={handleStartTracking}
            disabled={loading || !tripNickname.trim()}
            className="w-full h-16 text-xl font-bold bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all"
          >
            {loading ? 'Creating...' : 'Start Tracking'}
          </Button>

          <div className="text-center">
            <button
              onClick={() => navigate('/trips')}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
            >
              View all trips
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
